import { useEffect, useState, useRef, useCallback, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';
import "./styles/theme/global.css";

import ROUTES from "./config/routes";
import { ErrorBoundary, Layout, ScrollToTop } from "./components";
import { NavigationProvider } from "./context/NavigationContext";
import { useRouteTheme } from "./hooks/useTheme";
import { smoothScrollToElement } from "./utils/scrollUtils";

// Pages
// Pages — HomePage is eager for instant first paint; all others are lazy-loaded
import HomePage from "./pages/home/HomePage";
const AboutUsPage = lazy(() => import("./pages/about-us/AboutUsPage"));
const SwingVisionPage = lazy(() => import("./pages/swing-vision/SwingVisionPage"));
const QAlphaFlowPage = lazy(() => import("./pages/q-alpha-flow/QAlphaFlowPage"));
const OctopusSyncPage = lazy(() => import("./pages/octopus-sync/OctopusSyncPage"));
const SentinelPage = lazy(() => import("./pages/sentinel/SentinelPage"));
const ContactsPage = lazy(() => import("./pages/contacts/ContactsPage"));
const NotFoundPage = lazy(() => import("./pages/not-found/NotFoundPage"));
const HowToJoinPage = lazy(() => import("./pages/how-to-join/HowToJoinPage"));
const InterviewPage = lazy(() => import("./pages/how-to-join/InterviewPage"));
const InternshipPage = lazy(() => import("./pages/how-to-join/InternshipPage"));
const PreparingForInterviewPage = lazy(() => import("./pages/how-to-join/PreparingForInterviewPage"));
const RecruitmentPersonalDataPolicyEU = lazy(() => import("./pages/how-to-join/RecruitmentPersonalDataPolicyEU"));
const RecruitmentPersonalDataPolicyUK = lazy(() => import("./pages/how-to-join/RecruitmentPersonalDataPolicyUK"));
const JobDetailPage = lazy(() => import("./pages/how-to-join/JobDetailPage"));
const SitemapPage = lazy(() => import("./pages/sitemap/SitemapPage"));

const RouteWrapper = ({ component: Component, ...rest }) => {
  const location = useLocation();
  // Only remount on pathname changes, not hash changes
  return (
    <Component
      key={location.pathname + location.search}
      {...rest}
    />
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const theme = useRouteTheme(); // Get dynamic theme based on route

  // Removed unused navigation action state (was for old transitions)
  // const { pendingNavigationAction, setPendingNavigationAction } = useNavigation();

  const [displayLocation, setDisplayLocation] = useState(location);
  const pageSectionRefs = useRef({});

  useEffect(() => {
    if (displayLocation.key !== location.key) {
      setDisplayLocation(location);
    }
  }, [location, displayLocation.key]);
  useEffect(() => {
    // Handles hash scrolling when URL changes
    if (location.hash) {
      const id = location.hash.substring(1);
      // Delay scroll to allow DOM to render
      setTimeout(() => {
        smoothScrollToElement(id, 100);
      }, 100);
    }
  }, [location.pathname, location.hash]);
  const handleSetRefs = useCallback((refs) => {
    // Replace with the current page's refs to avoid stale observers from previous pages
    pageSectionRefs.current = refs || {};
  }, []);

  // ...existing code...

  const locationForRoutes = location;
  return (
    <>
      <ThemeProvider theme={theme}>
        <Layout refs={pageSectionRefs}>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress color="primary" /></Box>}>
          <Routes location={locationForRoutes}>
            <Route
              path={ROUTES.HOME}
              element={<HomePage setRefs={handleSetRefs} />}
            />
            <Route
              path={ROUTES.ABOUT_US}
              element={
                <RouteWrapper component={AboutUsPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.SWING_VISION}
              element={
                <RouteWrapper
                  component={SwingVisionPage}
                  setRefs={handleSetRefs}
                />
              }
            />
            <Route
              path={ROUTES.Q_ALPHA_FLOW}
              element={
                <RouteWrapper
                  component={QAlphaFlowPage}
                  setRefs={handleSetRefs}
                />
              }
            />
            <Route
              path={ROUTES.OCTOPUS_SYNC}
              element={
                <RouteWrapper
                  component={OctopusSyncPage}
                  setRefs={handleSetRefs}
                />
              }
            />
            <Route
              path={ROUTES.SENTINEL}
              element={
                <RouteWrapper
                  component={SentinelPage}
                  setRefs={handleSetRefs}
                />
              }
            />
            <Route
              path={ROUTES.HOW_TO_JOIN}
              element={
                <RouteWrapper component={HowToJoinPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/:jobId"}
              element={
                <RouteWrapper component={JobDetailPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/interview"}
              element={
                <RouteWrapper component={InterviewPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/internship"}
              element={
                <RouteWrapper component={InternshipPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/interview/preparing-for-interview"}
              element={<PreparingForInterviewPage />}
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/recruitment-personal-data-policy-eu"}
              element={<RecruitmentPersonalDataPolicyEU />}
            />
            <Route
              path={ROUTES.HOW_TO_JOIN + "/recruitment-personal-data-policy-uk"}
              element={<RecruitmentPersonalDataPolicyUK />}
            />
            <Route
              path={ROUTES.CONTACTS}
              element={
                <RouteWrapper component={ContactsPage} setRefs={handleSetRefs} />
              }
            />
            <Route
              path={ROUTES.SITEMAP}
              element={<RouteWrapper component={SitemapPage} setRefs={handleSetRefs} />}
            />
            <Route path="*" element={<NotFoundPage setRefs={handleSetRefs} />} />
          </Routes>
            </Suspense>
        </Layout>
      </ThemeProvider>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <Router>
      <ScrollToTop />
      {/* RiskGate might be auth-related, consider if it's still needed */}
      <NavigationProvider>
        <AppRoutes />
      </NavigationProvider>
    </Router>
  </ErrorBoundary>
);

export default App;
